import { useCallback } from "react"
import { useLocation, useNavigate, useParams } from "react-router"

import { useActiveOrganization } from "@/contexts"
import { RootPath, SitePath } from "@/models/route"

interface SwitchOrganizationOptions {
  replace?: boolean
}

// Site Explorer list tabs under `/{orgId}/sites/{tab}` — keep org-segment swap.
const SITE_EXPLORER_LIST_SEGMENTS = new Set<string>([
  SitePath.Compliance,
  SitePath.Overview,
  SitePath.Performance,
])

const getOrganizationSwitchPath = (
  pathname: string,
  activeOrgId: string | undefined,
  targetOrgId: string
): string | null => {
  if (!activeOrgId || !targetOrgId || activeOrgId === targetOrgId) {
    return null
  }

  const activeOrgPrefix = `/${activeOrgId}/`
  if (!pathname.startsWith(activeOrgPrefix)) {
    return null
  }

  const rest = pathname.slice(activeOrgPrefix.length)
  const [rootSegment, segmentAfterRoot] = rest.split("/")

  // `sites/{siteId}/...` (not a list tab) → Sites Overview for target org.
  if (
    rootSegment === RootPath.Site &&
    segmentAfterRoot &&
    !SITE_EXPLORER_LIST_SEGMENTS.has(segmentAfterRoot)
  ) {
    return `/${targetOrgId}/${RootPath.Site}/${SitePath.Overview}`
  }

  return `/${targetOrgId}/${rest}`
}

export const useSwitchOrganization = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const { activeOrganization } = useActiveOrganization()

  const switchToOrganization = useCallback(
    (targetOrgId: string, { replace }: SwitchOrganizationOptions = {}) => {
      const toUrl = getOrganizationSwitchPath(
        location.pathname,
        params.orgId ?? activeOrganization?.id,
        targetOrgId
      )

      if (!toUrl) {
        return
      }

      void navigate(toUrl, { replace })
    },
    [activeOrganization?.id, location.pathname, navigate, params.orgId]
  )

  return { switchToOrganization }
}
